export type Via = "start" | "next" | "previous" | "list" | "resume";
export type Layout = "panel" | "page";
export type Mode = "hidden" | "unfocused" | "idle" | "active";

export const IDLE_AFTER_MS = 60_000;
export const SUMMARY_MAX_BYTES = 12 * 1024;

export type DocumentDwell = {
  visit_id: string;
  final: boolean;
  document_id: string;
  via: Via;
  from_document_id: string | null;
  phase_elapsed_ms: number;
  duration_ms: number;
  active_ms: number;
  idle_ms: number;
  unfocused_ms: number;
  hidden_ms: number;
  max_depth: number;
  blocks: [block_id: string, on_screen_ms: number, first_seen_ms: number][];
  scroll: [t_ms: number, depth: number][];
  viewport: [w: number, h: number];
  layout: Layout;
};

type BlockTime = { ms: number; first: number | null; on: boolean };

export type Visit = {
  visitId: string;
  documentId: string;
  via: Via;
  fromDocumentId: string | null;
  phaseElapsedMs: number;
  openedAt: number;
  layout: Layout;
  viewport: [number, number];
  mode: Mode;
  lastAt: number;
  lastInputAt: number;
  lastScrollAt: number;
  activeMs: number;
  idleMs: number;
  unfocusedMs: number;
  hiddenMs: number;
  maxDepth: number;
  blocks: Map<string, BlockTime>;
  scroll: [number, number][];
};

export function layoutFor(docCount: number, width: number): Layout {
  return docCount > 1 && width >= 720 ? "panel" : "page";
}

/** Half of the block is in the viewport, or the block covers half of the viewport. */
export function blockIsOnScreen(
  block: { top: number; bottom: number },
  viewportHeight: number,
): boolean {
  const height = block.bottom - block.top;
  if (viewportHeight <= 0 || height <= 0) return false;
  const visible = Math.max(0, Math.min(block.bottom, viewportHeight) - Math.max(block.top, 0));
  if (visible <= 0) return false;
  if (visible / height >= 0.5) return true;
  return visible / viewportHeight >= 0.5;
}

export function openVisit(args: {
  visitId: string;
  documentId: string;
  via: Via;
  fromDocumentId: string | null;
  phaseElapsedMs: number;
  openedAt: number;
  layout: Layout;
  viewport: [number, number];
  mode?: Mode;
}): Visit {
  return {
    visitId: args.visitId,
    documentId: args.documentId,
    via: args.via,
    fromDocumentId: args.fromDocumentId,
    phaseElapsedMs: args.phaseElapsedMs,
    openedAt: args.openedAt,
    layout: args.layout,
    viewport: args.viewport,
    mode: args.mode ?? "active",
    lastAt: args.openedAt,
    lastInputAt: args.openedAt,
    lastScrollAt: args.openedAt - 1000,
    activeMs: 0,
    idleMs: 0,
    unfocusedMs: 0,
    hiddenMs: 0,
    maxDepth: 0,
    blocks: new Map(),
    scroll: [],
  };
}

export function accrue(visit: Visit, now: number): void {
  const dt = Math.max(0, now - visit.lastAt);
  if (dt === 0) return;
  if (visit.mode === "hidden") visit.hiddenMs += dt;
  else if (visit.mode === "unfocused") visit.unfocusedMs += dt;
  else if (visit.mode === "idle") visit.idleMs += dt;
  else visit.activeMs += dt;
  for (const block of visit.blocks.values()) {
    if (block.on) block.ms += dt;
  }
  visit.lastAt = now;
}

export function setMode(visit: Visit, mode: Mode, now: number): void {
  accrue(visit, now);
  visit.mode = mode;
}

export function noteInput(visit: Visit, now: number): void {
  accrue(visit, now);
  visit.lastInputAt = now;
  if (visit.mode === "idle") visit.mode = "active";
}

export function setVisibleBlocks(visit: Visit, ids: string[], now: number): void {
  accrue(visit, now);
  const next = new Set(ids);
  for (const [id, block] of visit.blocks) {
    block.on = next.has(id);
    next.delete(id);
  }
  for (const id of next) {
    visit.blocks.set(id, { ms: 0, first: Math.max(0, now - visit.openedAt), on: true });
  }
}

export function noteScroll(visit: Visit, depth: number, now: number): void {
  const clamped = Math.max(0, Math.min(1, depth));
  if (clamped > visit.maxDepth) visit.maxDepth = clamped;
  if (now - visit.lastScrollAt < 1000) return;
  visit.lastScrollAt = now;
  visit.scroll.push([Math.max(0, now - visit.openedAt), Math.round(clamped * 1000) / 1000]);
}

export function snapshot(visit: Visit, now: number, final: boolean): DocumentDwell {
  accrue(visit, now);
  const blocks: DocumentDwell["blocks"] = [];
  for (const [id, block] of visit.blocks) {
    if (block.first == null) continue;
    blocks.push([id, block.ms, block.first]);
  }
  return fitSummary({
    visit_id: visit.visitId,
    final,
    document_id: visit.documentId,
    via: visit.via,
    from_document_id: visit.fromDocumentId,
    phase_elapsed_ms: visit.phaseElapsedMs,
    duration_ms: visit.activeMs + visit.idleMs + visit.unfocusedMs + visit.hiddenMs,
    active_ms: visit.activeMs,
    idle_ms: visit.idleMs,
    unfocused_ms: visit.unfocusedMs,
    hidden_ms: visit.hiddenMs,
    max_depth: visit.maxDepth,
    blocks,
    scroll: visit.scroll.map((sample) => [sample[0], sample[1]]),
    viewport: [visit.viewport[0], visit.viewport[1]],
    layout: visit.layout,
  });
}

export function fitSummary(dwell: DocumentDwell): DocumentDwell {
  let scroll = dwell.scroll;
  while (scroll.length > 0 && byteLength({ ...dwell, scroll }) > SUMMARY_MAX_BYTES) {
    scroll = scroll.length === 1 ? [] : scroll.filter((_, index) => index % 2 === 0);
  }
  return { ...dwell, scroll };
}

function byteLength(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length;
}
